
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGDPR } from '@/contexts/GDPRContext';
import { DataManagement } from '@/components/gdpr/DataManagement';
import { ArrowLeft, Shield, Cookie, FileText, Settings } from 'lucide-react';

const GDPR: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showSettings, analyticsConsent, marketingConsent, functionalConsent } = useGDPR();

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
      <div className="container mx-auto py-8 px-4">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common:buttons.back')}
          </Button>
        </div>

        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Shield className="h-6 w-6" />
              {t('common:gdpr.title')}
            </CardTitle>
            <p className="text-gray-600">
              {t('common:gdpr.subtitle')}
            </p>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="cookies" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="cookies" className="flex items-center gap-2">
                  <Cookie className="h-4 w-4" />
                  {t('common:gdpr.tabs.cookies')}
                </TabsTrigger>
                <TabsTrigger value="data" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {t('common:gdpr.tabs.data')}
                </TabsTrigger>
                <TabsTrigger value="privacy" className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  {t('common:gdpr.tabs.privacy')}
                </TabsTrigger>
                <TabsTrigger value="legal" className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  {t('common:gdpr.tabs.legal')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="cookies" className="space-y-4">
                <h3 className="text-lg font-semibold">{t('common:gdpr.cookiesPreferences.title')}</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium">{t('common:gdpr.cookiesPreferences.functional.title')}</h4>
                      <p className="text-sm text-gray-600">{t('common:gdpr.cookiesPreferences.functional.description')}</p>
                    </div>
                    <span className="text-green-600 font-medium">{t('common:common.enabled')}</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium">{t('common:gdpr.cookiesPreferences.analytics.title')}</h4>
                      <p className="text-sm text-gray-600">{t('common:gdpr.cookiesPreferences.analytics.description')}</p>
                    </div>
                    <span className={`font-medium ${analyticsConsent ? 'text-green-600' : 'text-red-600'}`}>
                      {analyticsConsent ? t('common:common.enabled') : t('common:common.disabled')}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium">{t('common:gdpr.cookiesPreferences.marketing.title')}</h4>
                      <p className="text-sm text-gray-600">{t('common:gdpr.cookiesPreferences.marketing.description')}</p>
                    </div>
                    <span className={`font-medium ${marketingConsent ? 'text-green-600' : 'text-red-600'}`}>
                      {marketingConsent ? t('common:common.enabled') : t('common:common.disabled')}
                    </span>
                  </div>
                </div>
                
                <Button onClick={showSettings} className="w-full">
                  {t('common:gdpr.cookiesPreferences.modifyButton')}
                </Button>
              </TabsContent>

              <TabsContent value="data" className="space-y-4">
                <h3 className="text-lg font-semibold">{t('common:gdpr.dataManagement.title')}</h3>
                <DataManagement />
              </TabsContent>

              <TabsContent value="privacy" className="space-y-4">
                <h3 className="text-lg font-semibold">{t('common:gdpr.privacyPolicy.title')}</h3>
                <p className="text-gray-600 mb-4">
                  {t('common:gdpr.privacyPolicy.description')}
                </p>
                <Button onClick={() => navigate('/privacy')} variant="outline">
                  {t('common:gdpr.privacyPolicy.button')}
                </Button>
              </TabsContent>

              <TabsContent value="legal" className="space-y-4">
                <h3 className="text-lg font-semibold">{t('common:gdpr.legalNotice.title')}</h3>
                <p className="text-gray-600 mb-4">
                  {t('common:gdpr.legalNotice.description')}
                </p>
                <Button onClick={() => navigate('/legal')} variant="outline">
                  {t('common:gdpr.legalNotice.button')}
                </Button>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GDPR;
